import { Skeleton, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Editor, Modal } from 'components/common';
import { useGetCustomerSupportInformationActiveDetailQuery } from 'services';
import { calculateColumnWidths, CUSTOMER_SUPPORT_INFORMATION_FORMAT } from 'utils';
import CustomerSupportInformationActiveExcel from './CustomerSupportInformationActiveExcel';
import ElectricalRecordingSchedules from './ElectricalRecodingSchedules';
import ElectricityPaymentLocations from './ElectricityPaymentLocations';

type CustomerSupportInformationDetailProps = {
  customerSupportInformationId?: number;
  open: boolean;
  onCancel: () => void;
};

const CustomerSupportInformationDetail = ({
  onCancel,
  open,
  customerSupportInformationId
}: CustomerSupportInformationDetailProps) => {
  const [width, setWidth] = useState(1032);
  const { data: customerSupportInformation, isLoading } = useGetCustomerSupportInformationActiveDetailQuery(
    customerSupportInformationId!,
    {
      skip: !customerSupportInformationId,
      refetchOnMountOrArgChange: true
    }
  );
  useEffect(() => {
    if (!open) setWidth(1032);
  }, [open]);

  const onGetDataExcel = (data: (string | number)[][]) => {
    const calculateData = calculateColumnWidths(data, 1032);
    const totalWidth = calculateData.totalWidth + 48;
    const maxWidth = window.innerWidth - 80;
    const width = Math.min(totalWidth, maxWidth ?? 9999);
    if (width) {
      setWidth(width);
    } else {
      setWidth(totalWidth);
    }
  };

  const getModalWidth = (): number => {
    switch (customerSupportInformation?.data.formatId) {
      case CUSTOMER_SUPPORT_INFORMATION_FORMAT.ELECTRICAL_RECORDING_SCHEDULE:
        return width;
      case CUSTOMER_SUPPORT_INFORMATION_FORMAT.ELECTRICITY_PAYMENT_LOCATION:
        return width;
      case CUSTOMER_SUPPORT_INFORMATION_FORMAT.EXCEL_TABLE:
        return width;
      default:
        return 1200;
    }
  };

  const renderContent = () => {
    if (
      customerSupportInformation?.data &&
      customerSupportInformation.data.customerSupportInformationId === customerSupportInformationId
    ) {
      switch (customerSupportInformation?.data.formatId) {
        case CUSTOMER_SUPPORT_INFORMATION_FORMAT.ELECTRICAL_RECORDING_SCHEDULE:
          return <ElectricalRecordingSchedules showExport={false} />;
        case CUSTOMER_SUPPORT_INFORMATION_FORMAT.ELECTRICITY_PAYMENT_LOCATION:
          return <ElectricityPaymentLocations showExport={false} />;
        case CUSTOMER_SUPPORT_INFORMATION_FORMAT.EXCEL_TABLE:
          return (
            <CustomerSupportInformationActiveExcel
              minWidth={1032}
              maxWidth={window.innerWidth - 200}
              height={window.innerHeight - 124}
              customerSupportInformationId={customerSupportInformationId}
              onLoadData={(data) => onGetDataExcel(data)}
              title={customerSupportInformation.data.title}
              showExport={false}
              fileExcelId={customerSupportInformation.data.fileId}
            />
          );
        case CUSTOMER_SUPPORT_INFORMATION_FORMAT.TEXT:
          const screenHeight = window.screen.height;
          return (
            <div className='-mx-6 mb-4 flex flex-1 flex-col px-6'>
              <Editor
                value={customerSupportInformation.data.content}
                disabled={true}
                init={{
                  height: screenHeight - 356,
                  menubar: false, // Ẩn menu bar
                  toolbar: false, // Ẩn toolbar
                  toolbar1: '',
                  statusbar: false, // Ẩn status bar
                  plugins: [], // Không sử dụng plugin nào,
                  content_style: `
                    body { 
                      background-color: #ffff; 
                      border: none; 
                       
                      font-size: 16px; 
                      line-height: 1.6;
                      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                    }
                    table { 
                      width: 100% !important; 
                      border-collapse: collapse;
                      margin: 15px 0;
                    }
                    table td, table th {
                      padding: 8px 22px;
                      border: 1px solid #ddd;
                    }
                    p { margin: 10px 0; }
                    .mce-content-body { max-width: 100%; }
                  `

                }}
                toolbar={false}
              />
            </div>
          );
        default:
          return (
            <Link to={customerSupportInformation?.data.content} target='_blank'>
              <Typography.Link>{customerSupportInformation?.data.content}</Typography.Link>
            </Link>
          );
      }
    }
    return null;
  };
  return (
    <Modal.Small
      width={getModalWidth()}
      destroyOnClose
      centered
      onCancel={onCancel}
      open={open}
      title={customerSupportInformation?.data.title}
      footer={null}
      styles={{
        body: {
          padding:
            customerSupportInformation?.data.formatId === CUSTOMER_SUPPORT_INFORMATION_FORMAT.EXCEL_TABLE
              ? '0 24px 20px 24px'
              : customerSupportInformation?.data.formatId === CUSTOMER_SUPPORT_INFORMATION_FORMAT.TEXT
                ? '0'
                : 24,
          minHeight: 'calc(100vh - 156px)',
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto'
        },
        header: {
          padding: '16px 24px',
          marginBottom: customerSupportInformation?.data.formatId === CUSTOMER_SUPPORT_INFORMATION_FORMAT.TEXT ? '0' : undefined
        },
        content: {
          padding: customerSupportInformation?.data.formatId === CUSTOMER_SUPPORT_INFORMATION_FORMAT.TEXT ? '0' : undefined
        }
      }}
    >
      {isLoading ? <Skeleton title={false} paragraph={{ rows: 5 }} /> : <>{renderContent()}</>}
    </Modal.Small>
  );
};
export default CustomerSupportInformationDetail;
